// Reconnect policy decisions for MQTTSubscriber, extracted as pure functions so they're testable
// without the browser/mqtt/redux machinery.

/**
 * Whether to re-fetch live data after a CONNACK, decided by the broker's authoritative
 * `sessionPresent` flag rather than a client-side timer.
 *
 * With cleanStart=false, the broker sets sessionPresent=true iff it still held our session and is
 * replaying its queued deltas — so nothing was missed. sessionPresent=false means the session was
 * gone (reaped past expiry, taken over by another connection, or lost to a broker restart), so any
 * downstream deltas published while we were away are lost and we must refetch from the DB.
 *
 * Skipped on the FIRST connect of a realm: the page-load / post-auth bootstrap already fetched, and
 * a first connect always reports sessionPresent=false (no prior session) without having missed anything.
 *
 * @param connectCount   number of successful CONNACKs this realm has seen (1 = first connect)
 * @param sessionPresent the CONNACK sessionPresent flag (undefined treated as "not resumed" → safe refetch)
 */
export function shouldResyncOnConnect(connectCount: number, sessionPresent: boolean | undefined): boolean {
  return connectCount > 1 && sessionPresent !== true;
}

/**
 * Whether `now` falls inside a debounce window that started at `lastAt`. Used to collapse a resync
 * flap (the same realm reconnecting repeatedly) and to skip a refetch that lands right after a recent
 * one. `lastAt === 0` means "never happened" → not within the window (so the first event always runs).
 */
export function isWithinDebounce(lastAt: number, now: number, windowMs: number): boolean {
  return lastAt > 0 && now - lastAt < windowMs;
}
